/* The Client Cockpit composition seam (client-first IA increment 2, spec §2;
 * CTO ruling Part-1 §3 "KEPT (repointed)").
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ THE /overview SWAP SEAM.                                                  │
 * │ cockpitData(slug) is the ONE place the cockpit assembles its data. Today  │
 * │ (v1, on-TRUNK endpoints only) it composes from:                          │
 * │   • GET /clients            — the card rollup (brand, planes, counts,     │
 * │                               attention, lastRunFile) → header + freshness │
 * │   • the existing latest-run resolution (fetch the client's lastRunFile →  │
 * │     loadPayload) — drives the section renderers (zone 4), the derive      │
 * │     findings (zone 2) and WS's per-client task board (zone 3).            │
 * │   • WS (GET /clients/{slug}/tasks) — reached via loadPayload's WS.init(); │
 * │     panels read live payloadStore + WS at render time (subscriptions).    │
 * │ LATER: replace this function body with a single GET /clients/{slug}/      │
 * │ overview call (feat/client-external-data-sources) — the aspect-composed   │
 * │ response the CTO ruling specs. Callers depend ONLY on the CockpitData     │
 * │ shape below, so the swap is a feed change, not a view rewrite.           │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * We deliberately do NOT build on GET /clients/{slug}/overview here — it lives
 * on an UNMERGED branch (per the dispatch + CTO note); v1 is trunk-only.
 */
import { request } from '../../api/client.ts';
import { rawUrl } from '../../api/client.ts';
import { beginLoad, isLoadCurrent, loadPayload, payloadStore } from '../../state/payload.ts';

/** Per-plane staleness the /clients rollup carries (ws_profiles.py::portfolio),
 * mirrored from views/myClients.ts. */
export interface CockpitPlaneState {
  lastRun: string | null;
}

/** One client row from GET /clients — the SAME rollup My Clients renders. */
export interface CockpitClientRow {
  slug: string;
  brand: string;
  /** brand-visual source (GET /clients rollup) — the SAME domain/logoUrl the
   * register row renders through brandVisual(); the presence hub mark reuses it
   * so a client's mark is identical everywhere (DESIGN-FIDELITY P0-3). */
  domain?: string | null;
  logoUrl?: string | null;
  attention: string;
  attentionScore: number;
  open: number;
  high: number;
  pendingVerification: number;
  attested: number;
  planes?: { presence?: CockpitPlaneState; search?: CockpitPlaneState; ai?: CockpitPlaneState };
  lastRun?: string | null;
  lastRunFile?: string | null;
}

export interface CockpitData {
  slug: string;
  /** the rollup row, or null when the client isn't indexed / rollup unreachable. */
  row: CockpitClientRow | null;
  /** true once this client's latest run payload is loaded into payloadStore
   * (or honestly: there was none to load). */
  payloadLoaded: boolean;
  /** true when the client has no research run yet (honest empty cockpit). */
  noResearch: boolean;
  /** set when the rollup itself couldn't be reached (service down). */
  error?: string;
}

/** Fetch a run archive as text and drive the existing render pipeline
 * (loadPayload → renderAll + WS.init). Mirrors main.ts/boot()'s own fetch.
 *
 * FIX-audit F3: the load `token` is allocated by the CALLER at navigation start
 * (initCockpit), NOT here — allocating it inside this function (i.e. after
 * cockpitData's own `/clients` await) let a slower-resolving earlier navigation
 * become the newest generation and win. The token is threaded in and the commit
 * is gated on isLoadCurrent(token). */
async function loadRunPayload(lastRunFile: string, token: number): Promise<boolean> {
  try {
    const res = await fetch(rawUrl(`/test-runs/${lastRunFile}`));
    if (!res.ok) return false;
    const text = await res.text();
    if (!isLoadCurrent(token)) return false; // superseded — do not commit
    loadPayload(text, lastRunFile);
    return true;
  } catch {
    return false;
  }
}

/** Compose the cockpit's data for a slug. Idempotent per RUN: if the rollup's
 * lastRunFile is already the loaded payload (state.runFile — tab switches
 * within one cockpit), it does NOT reload; any other loaded payload (another
 * client, the boot demo, an older run of the same client) forces the reload. */
export async function cockpitData(slug: string, token: number = beginLoad()): Promise<CockpitData> {
  let row: CockpitClientRow | null = null;
  try {
    const rows = (await request<{ clients?: CockpitClientRow[] }>('/clients')).clients || [];
    row = rows.find((r) => r.slug === slug) || null;
  } catch (e) {
    return { slug, row: null, payloadLoaded: false, noResearch: false, error: (e as Error).message };
  }

  if (!row) {
    // Not in the rollup — could be a brand-new registration or a bad slug.
    return { slug, row: null, payloadLoaded: false, noResearch: true };
  }

  const lastRunFile = row.lastRunFile || null;
  if (!lastRunFile) {
    return { slug, row, payloadLoaded: false, noResearch: true };
  }

  // Reuse guard keyed on the RUN, never the client (design review 2026-07-12,
  // BLOCKER fix 1): client-match alone is never sufficient — the boot-seeded
  // demo payload carries the same brand, so a client-keyed guard served the
  // WRONG RUN's numbers under the right freshness stamp. state.runFile is
  // stamped by loadPayload() from the archive filename (the /chat blocker-6
  // plumbing) — reload unless the loaded run IS the rollup's lastRunFile. Tab
  // switches within one cockpit still skip the reload (runFile matches after
  // the first load).
  let payloadLoaded = payloadStore.get().runFile === lastRunFile;
  if (!payloadLoaded) payloadLoaded = await loadRunPayload(lastRunFile, token);

  return { slug, row, payloadLoaded, noResearch: false };
}
